/**
 * Client Portal V2 - Reusable Components
 * ======================================
 */

import React from "react";
import { Link } from "react-router-dom";
import { 
  AlertTriangle, CheckCircle, Clock, ChevronRight, 
  Download, FileText, ExternalLink, Loader2
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

// ============================================================
// STATUS BADGE
// ============================================================

const stageColors = {
  NEW: "bg-slate-100 text-slate-700",
  AWAITING_QUOTE: "bg-blue-100 text-blue-700",
  QUOTE_RECEIVED: "bg-blue-100 text-blue-700",
  AWAITING_CLIENT_APPROVAL: "bg-orange-100 text-orange-700 ring-2 ring-orange-300",
  APPROVED: "bg-green-100 text-green-700",
  ASSIGNED: "bg-purple-100 text-purple-700",
  SCHEDULED: "bg-purple-100 text-purple-700",
  IN_PROGRESS: "bg-indigo-100 text-indigo-700",
  COMPLETED: "bg-emerald-100 text-emerald-700",
  AWAITING_DOCUMENTS: "bg-yellow-100 text-yellow-700",
  READY_TO_INVOICE: "bg-teal-100 text-teal-700",
  INVOICED: "bg-teal-100 text-teal-700",
  CLOSED: "bg-slate-100 text-slate-600",
  CANCELLED: "bg-red-100 text-red-700",
  REJECTED: "bg-red-100 text-red-700",
};

export function ClientStatusBadge({ stage, label, className = "" }) {
  const colorClass = stageColors[stage] || "bg-slate-100 text-slate-700";
  
  return (
    <Badge className={`${colorClass} font-medium ${className}`}>
      {label || stage?.replace(/_/g, " ")}
    </Badge>
  );
}

// ============================================================
// KPI CARD
// ============================================================

export function ClientKpiCard({ title, value, icon: Icon, color = "teal", trend, onClick, alert }) {
  const colorMap = {
    teal: "from-teal-500 to-teal-600",
    blue: "from-blue-500 to-blue-600",
    amber: "from-amber-500 to-amber-600",
    red: "from-red-500 to-red-600",
    green: "from-green-500 to-green-600",
    purple: "from-purple-500 to-purple-600",
  };

  return (
    <div
      className={`relative overflow-hidden rounded-2xl bg-white p-5 shadow-sm border border-slate-100 transition-all hover:shadow-md ${onClick ? "cursor-pointer" : ""} ${alert ? "ring-2 ring-red-200" : ""}`}
      onClick={onClick}
      data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-500 font-medium">{title}</p>
          <p className="mt-2 text-3xl font-bold text-slate-900">{value ?? 0}</p>
          {trend !== undefined && (
            <p className={`mt-1 text-xs ${trend > 0 ? "text-red-600" : trend < 0 ? "text-green-600" : "text-slate-400"}`}>
              {trend > 0 ? `+${trend}` : trend} from last week
            </p>
          )}
        </div>
        <div className={`p-3 rounded-xl bg-gradient-to-br ${colorMap[color]} shadow-lg`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
      {onClick && (
        <div className="absolute bottom-3 right-3">
          <ChevronRight className="w-4 h-4 text-slate-300" />
        </div>
      )}
      {alert && (
        <div className="absolute top-2 right-2">
          <AlertTriangle className="w-4 h-4 text-red-500" />
        </div>
      )}
    </div>
  );
}

// ============================================================
// NEXT ACTION BANNER
// ============================================================

export function ClientNextActionBanner({ action, owner, urgent, link }) {
  if (!action) return null;

  const bgClass = urgent 
    ? "bg-gradient-to-r from-orange-500 to-orange-600" 
    : "bg-gradient-to-r from-teal-600 to-teal-700";

  return (
    <div className={`${bgClass} rounded-xl p-4 text-white`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {urgent ? (
            <AlertTriangle className="w-5 h-5" />
          ) : (
            <Clock className="w-5 h-5" />
          )}
          <div>
            <p className="font-semibold">{action}</p>
            {owner && <p className="text-sm opacity-80">Responsible: {owner}</p>}
          </div>
        </div>
        {link && (
          <Link
            to={link}
            className="px-4 py-2 bg-white/20 rounded-lg text-sm font-medium hover:bg-white/30 transition-colors"
          >
            Take Action
          </Link>
        )}
      </div>
    </div>
  );
}

// ============================================================
// ALERT BANNER
// ============================================================

export function ClientAlertBanner({ alerts }) {
  if (!alerts || alerts.length === 0) return null;

  const critical = alerts.filter(a => a.severity === "critical");
  const high = alerts.filter(a => a.severity === "high");

  const displayAlerts = [...critical, ...high].slice(0, 3);

  if (displayAlerts.length === 0) return null;

  return (
    <div className="space-y-2 mb-6">
      {displayAlerts.map((alert, idx) => (
        <Link
          key={idx}
          to={alert.link || "#"}
          className={`flex items-center gap-3 p-4 rounded-xl ${
            alert.severity === "critical" 
              ? "bg-red-50 border border-red-200" 
              : "bg-orange-50 border border-orange-200"
          }`}
        >
          <AlertTriangle className={`w-5 h-5 ${
            alert.severity === "critical" ? "text-red-600" : "text-orange-600"
          }`} />
          <span className={`flex-1 text-sm font-medium ${
            alert.severity === "critical" ? "text-red-800" : "text-orange-800"
          }`}>
            {alert.title || alert.message}
          </span>
          <ChevronRight className="w-4 h-4 text-slate-400" />
        </Link>
      ))}
    </div>
  );
}

// ============================================================
// EMPTY STATE
// ============================================================

export function ClientEmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="text-center py-12 px-4">
      {Icon && (
        <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
          <Icon className="w-8 h-8 text-slate-400" />
        </div>
      )}
      <h3 className="text-lg font-semibold text-slate-900 mb-2">{title}</h3>
      {description && <p className="text-slate-500 mb-4">{description}</p>}
      {action}
    </div>
  );
}

// ============================================================
// LOADING STATE
// ============================================================

export function ClientLoadingState({ message = "Loading..." }) {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="text-center">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin mx-auto mb-3" />
        <p className="text-slate-500">{message}</p>
      </div>
    </div>
  );
}

// ============================================================
// ERROR STATE
// ============================================================

export function ClientErrorState({ message, onRetry }) {
  return (
    <div className="text-center py-12 px-4">
      <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center mx-auto mb-4">
        <AlertTriangle className="w-8 h-8 text-red-600" />
      </div>
      <h3 className="text-lg font-semibold text-slate-900 mb-2">Something went wrong</h3>
      <p className="text-slate-500 mb-4">{message}</p>
      {onRetry && (
        <Button onClick={onRetry} variant="outline">
          Try Again
        </Button>
      )}
    </div>
  );
}

// ============================================================
// DOCUMENT LIST ITEM
// ============================================================

export function ClientDocumentItem({ doc, compact = false }) {
  const iconMap = {
    certificate: FileText,
    photo: FileText,
    invoice: Receipt,
    quote: FileText,
  };

  const Icon = iconMap[doc.category] || FileText;

  if (compact) {
    return (
      <a
        href={doc.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700"
      >
        <Download className="w-4 h-4" />
        {doc.name}
      </a>
    );
  }

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-slate-300 bg-white">
      <div className="p-2 rounded-lg bg-slate-100">
        <Icon className="w-5 h-5 text-slate-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-900 truncate">{doc.name}</p>
        <p className="text-xs text-slate-500">{doc.type} • {doc.uploaded_at?.split("T")[0]}</p>
      </div>
      <a
        href={doc.url}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"
      >
        <Download className="w-4 h-4" />
      </a>
    </div>
  );
}

// ============================================================
// INVOICE VARIANCE BADGE
// ============================================================

export function ClientInvoiceVarianceBadge({ variance, variancePercent }) {
  if (!variance || Math.abs(variancePercent) <= 2) {
    return <Badge className="bg-green-100 text-green-700">On Budget</Badge>;
  }

  if (variance > 0) {
    return (
      <Badge className="bg-red-100 text-red-700">
        +{variancePercent.toFixed(1)}% Over
      </Badge>
    );
  }

  return (
    <Badge className="bg-blue-100 text-blue-700">
      {variancePercent.toFixed(1)}% Under
    </Badge>
  );
}

// ============================================================
// TIMELINE
// ============================================================

export function ClientTimeline({ items }) {
  if (!items || items.length === 0) {
    return (
      <p className="text-sm text-slate-500 italic">No activity recorded</p>
    );
  }

  return (
    <div className="space-y-4">
      {items.map((item, idx) => (
        <div key={idx} className="flex gap-3">
          <div className="relative">
            <div className={`w-2 h-2 rounded-full mt-2 ${
              item.type === "status_change" ? "bg-teal-500" : "bg-slate-300"
            }`} />
            {idx < items.length - 1 && (
              <div className="absolute top-4 left-1/2 -translate-x-1/2 w-px h-full bg-slate-200" />
            )}
          </div>
          <div className="flex-1 pb-4">
            <p className="text-sm text-slate-900">{item.content}</p>
            <p className="text-xs text-slate-500 mt-1">
              {item.author && `${item.author} • `}
              {item.timestamp?.split("T")[0]}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}
